/**
 * MediaRecorder lifecycle for the viewfinder.
 *
 * The recorder can be driven from tests without a camera.
 */

import { coverCrop } from './cameraCrop'

/**
 * Safari records MP4/H.264 (fragmented MP4 since 18.4); Chrome and Android record
 * WebM/VP8-9. We do not transcode client-side, so we take whatever the device produces
 * and let the server pipeline normalize it.
 */
export function pickSupportedMime() {
  const candidates = [
    'video/mp4;codecs=h264,aac',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm'
  ]

  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return ''
  }
  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate
  }
  return ''
}

/** Normalize a codec-suffixed mime to the base content type the ANAPI allowlist expects. */
export function normalizeMime(mime) {
  const value = (mime || '').toLowerCase()
  if (value.includes('mp4')) return { type: 'video/mp4', ext: '.mp4' }
  return { type: 'video/webm', ext: '.webm' }
}

/**
 * WebKit (iOS Safari / desktop Safari) frequently produces empty MediaRecorder blobs
 * when fed cloned tracks. Prefer the live preview stream there; Chromium still clones
 * so the live <video> does not freeze while recording.
 */
export function prefersUnclonedRecordingStream(userAgent, extras = {}) {
  const ua =
    userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '')
  const maxTouchPoints =
    extras.maxTouchPoints ??
    (typeof navigator !== 'undefined' ? navigator.maxTouchPoints || 0 : 0)
  const platform =
    extras.platform ?? (typeof navigator !== 'undefined' ? navigator.platform || '' : '')

  const isSafari = /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|Edg|Android/i.test(ua)
  // iPadOS 13+ can report as MacIntel with touch. Only treat that as WebKit when the
  // browser is actually Safari — Chrome-on-Mac must still clone to protect the preview.
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) || (isSafari && platform === 'MacIntel' && maxTouchPoints > 1)
  return isIOS || isSafari
}

/**
 * Clone tracks for MediaRecorder so the live <video> preview keeps its own
 * consumer on Chromium. Sharing one MediaStream between preview + recorder freezes
 * the viewfinder on Chrome desktop (and some Android WebViews).
 */
export function cloneStreamForRecording(stream) {
  if (!stream) return null
  if (prefersUnclonedRecordingStream()) return stream

  const tracks = typeof stream.getTracks === 'function' ? stream.getTracks() : []
  if (!tracks.length) return stream

  try {
    return new MediaStream(tracks.map((track) => (typeof track.clone === 'function' ? track.clone() : track)))
  } catch {
    return stream
  }
}

function centerCrop(width, height, zoom) {
  return coverCrop(width, height, { zoom })
}

/**
 * Draw the live preview into a canvas (optional mirror + digital zoom) and return a
 * MediaStream suitable for MediaRecorder. CSS transforms on the preview <video> do not
 * affect drawImage, so mirroring must be applied here for the saved clip to match what
 * the guest composed.
 */
export function createTransformedRecordingStream({
  sourceStream,
  videoElement,
  mirror = false,
  digitalZoom = 1,
  onFrame = null
}) {
  if (!sourceStream || !videoElement || typeof document === 'undefined') return null
  if (typeof HTMLCanvasElement === 'undefined' || !HTMLCanvasElement.prototype.captureStream) {
    return null
  }

  const videoTrack = sourceStream.getVideoTracks?.()[0]
  const settings = videoTrack?.getSettings?.() || {}
  const srcW = Math.max(2, Math.round(settings.width || videoElement.videoWidth || 1080))
  const srcH = Math.max(2, Math.round(settings.height || videoElement.videoHeight || 1920))
  const crop = centerCrop(srcW, srcH, digitalZoom)
  const width = Math.max(2, Math.round(crop.sw))
  const height = Math.max(2, Math.round(crop.sh))
  const fps = Math.min(30, Math.max(15, Math.round(settings.frameRate || 30)))

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  let stopped = false
  let rafId = 0

  const draw = () => {
    if (stopped) return
    const vw = videoElement.videoWidth || width
    const vh = videoElement.videoHeight || height
    if (vw > 0 && vh > 0) {
      const { sx, sy, sw, sh } = centerCrop(vw, vh, digitalZoom)
      ctx.save()
      if (mirror) {
        ctx.translate(width, 0)
        ctx.scale(-1, 1)
      }
      ctx.drawImage(videoElement, sx, sy, sw, sh, 0, 0, width, height)
      ctx.restore()
      onFrame?.(ctx)
    }
    rafId = window.requestAnimationFrame(draw)
  }
  rafId = window.requestAnimationFrame(draw)

  const canvasStream = canvas.captureStream(fps)
  const audioTracks = typeof sourceStream.getAudioTracks === 'function' ? sourceStream.getAudioTracks() : []
  audioTracks.forEach((track) => {
    try {
      canvasStream.addTrack(typeof track.clone === 'function' ? track.clone() : track)
    } catch {
      // Audio is best-effort; a silent clip is still usable.
    }
  })

  return {
    stream: canvasStream,
    stop() {
      stopped = true
      if (rafId) window.cancelAnimationFrame(rafId)
      rafId = 0
      try {
        canvasStream.getTracks().forEach((track) => {
          try {
            track.stop()
          } catch {
            // already ended
          }
        })
      } catch {
        // ignore
      }
    }
  }
}

export default class ClipRecorder {
  /**
   * @param {{ stream: MediaStream, videoElement?: HTMLVideoElement|null,
   *           mirror?: boolean, digitalZoom?: number, bitrate?: number,
   *           timesliceMs?: number, onTick?: (elapsedSeconds: number) => void }} options
   */
  constructor({
    stream,
    videoElement = null,
    mirror = false,
    digitalZoom = 1,
    bitrate = 0,
    timesliceMs = 1000,
    onTick = null
  }) {
    this._sourceStream = stream
    this._videoElement = videoElement
    this._mirror = !!mirror
    this._digitalZoom = Math.max(1, Number(digitalZoom) || 1)
    this._bitrate = bitrate
    this._timesliceMs = timesliceMs
    this._onTick = onTick

    this._stream = null
    this._ownsStream = false
    this._transformPipeline = null
    this._recorder = null
    this._chunks = []
    this._bytes = 0
    this._mime = ''
    this._startedAt = 0
    this._tickId = null
    this._stopPromise = null
  }

  get isRecording() {
    return !!this._recorder && this._recorder.state === 'recording'
  }

  get elapsedSeconds() {
    if (!this._startedAt) return 0
    return (Date.now() - this._startedAt) / 1000
  }

  /** Bytes flushed so far. Only meaningful because we record with a timeslice. */
  get bytes() {
    return this._bytes
  }

  _adoptRecordStream() {
    if (this._videoElement) {
      this._transformPipeline = createTransformedRecordingStream({
        sourceStream: this._sourceStream,
        videoElement: this._videoElement,
        mirror: this._mirror,
        digitalZoom: this._digitalZoom
      })
      if (this._transformPipeline?.stream) {
        this._stream = this._transformPipeline.stream
        this._ownsStream = true
        return
      }
    }

    // Prefer a clone so the caller's preview stream is untouched — except on WebKit,
    // where clones commonly yield empty recordings.
    this._stream = cloneStreamForRecording(this._sourceStream) || this._sourceStream
    this._ownsStream = this._stream !== this._sourceStream
  }

  start() {
    this._adoptRecordStream()
    if (!this._stream) throw new Error('Recording is not available right now.')

    const mimeType = pickSupportedMime()
    const options = {}
    if (mimeType) options.mimeType = mimeType
    if (this._bitrate > 0) options.videoBitsPerSecond = this._bitrate

    try {
      this._recorder = new MediaRecorder(this._stream, options)
    } catch {
      // Some devices reject the options object wholesale; a bare recorder still works.
      this._recorder = new MediaRecorder(this._stream)
    }

    this._chunks = []
    this._bytes = 0
    this._mime = this._recorder.mimeType || mimeType || 'video/webm'

    this._recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        this._chunks.push(event.data)
        this._bytes += event.data.size
      }
    }

    this._recorder.onerror = () => {
      // Surface via stop() when the guest finishes; keep ticking so the UI stays honest.
    }

    // A timeslice flushes progressively instead of holding the whole clip in one buffer,
    // which keeps memory flat on long takes and gives us a live byte count.
    this._recorder.start(this._timesliceMs)
    this._startedAt = Date.now()

    if (this._onTick) {
      this._onTick(0)
      this._tickId = window.setInterval(() => this._onTick(this.elapsedSeconds), 200)
    }
  }

  /**
   * @returns {Promise<{ blob: Blob, type: string, ext: string, durationSecs: number, bytes: number }>}
   */
  stop() {
    if (this._stopPromise) return this._stopPromise

    this._stopPromise = new Promise((resolve, reject) => {
      if (!this._recorder) {
        reject(new Error('Nothing was recorded.'))
        return
      }

      const durationSecs = Math.max(0, Math.round(this.elapsedSeconds))

      this._recorder.onstop = () => {
        this._clearTick()
        this._releaseRecordingResources()
        const { type, ext } = normalizeMime(this._mime)
        const blob = new Blob(this._chunks, { type })
        this._chunks = []

        // Empty / tiny blobs are the classic "black 0s review" failure mode (often from
        // recording cloned tracks on WebKit). Fail loudly so the guest can retry.
        if (!blob.size) {
          reject(new Error('Recording produced no video. Please try again.'))
          return
        }

        resolve({
          blob,
          type,
          ext,
          durationSecs,
          bytes: blob.size,
          startedAt: this._startedAt
        })
      }

      if (this._recorder.state === 'inactive') {
        this._recorder.onstop()
        return
      }

      try {
        // Flush the final timeslice before stop; some engines otherwise drop the last second.
        if (typeof this._recorder.requestData === 'function' && this._recorder.state === 'recording') {
          this._recorder.requestData()
        }
        this._recorder.stop()
      } catch (error) {
        this._clearTick()
        this._releaseRecordingResources()
        reject(error)
      }
    })

    return this._stopPromise
  }

  _clearTick() {
    if (this._tickId) {
      window.clearInterval(this._tickId)
      this._tickId = null
    }
  }

  _releaseRecordingResources() {
    if (this._transformPipeline) {
      try {
        this._transformPipeline.stop()
      } catch {
        // ignore
      }
      this._transformPipeline = null
      this._stream = null
      this._ownsStream = false
      return
    }

    if (!this._ownsStream || !this._stream) return
    try {
      this._stream.getTracks?.().forEach((track) => {
        try {
          track.stop()
        } catch {
          // already ended
        }
      })
    } catch {
      // ignore
    }
    this._stream = null
    this._ownsStream = false
  }

  /** Tear down without producing a clip. */
  cancel() {
    this._clearTick()
    if (this._recorder && this._recorder.state !== 'inactive') {
      this._recorder.ondataavailable = null
      this._recorder.onstop = null
      try {
        this._recorder.stop()
      } catch {
        // already stopped
      }
    }
    this._recorder = null
    this._chunks = []
    this._stopPromise = null
    this._releaseRecordingResources()
  }
}
